/**
 * Game tuning constants.
 *
 * Ported from `src/Constantes.h` (2013). Back then you had to recompile to
 * change these; here `GameConfig` is an object the Setup menu will edit at
 * runtime (v0.5).
 */

import type { PlayerColor } from "../sim/types";
import { Direction } from "../sim/types";

export interface SpawnPoint {
  x: number;
  y: number;
  dir: Direction;
}

export interface GameConfig {
  /** Play area (bounds), from `GridConstructeur(&grille, 5, 5, 1000, 700, ...)`. */
  grid: { x: number; y: number; width: number; height: number };
  /** Simulation step length in seconds. `intervalle_horloge = 0.05f` -> 20 Hz. */
  tickSeconds: number;
  /** Initial bike speed in px/tick. `_Vitesse_Initiale`. */
  initialSpeed: number;
  /** Acceleration in px/tick². `_Acceleration`. */
  acceleration: number;
  /** Initial lifetime of a trail segment, in ticks. `_Duree_Vie_Mur`. */
  trailLife: number;
  /** Rounds needed to win the match. `_Score_de_Victoire`. */
  victoryScore: number;
  /** Bonus tuning, ported from `Constantes.h`. */
  bonus: BonusConfig;
}

export interface BonusConfig {
  /** Max bonuses on the grid at once. `_Nombre_de_Bonus`. */
  maxOnGrid: number;
  /** Square side of a bonus pickup, px. */
  size: number;
  /** Ticks before the first spawn attempt. C: `rand() % 150`. */
  respawnFirst: number;
  /** Ticks between later spawn attempts: `rand() % (max-min) + min`. C: `rand()%230 + 20`. */
  respawnMin: number;
  respawnMax: number;
  /** BOOST: extra speed and how long it lasts once triggered. `_Vitesse_Boost` / `_Temps_Bonus_Boost`. */
  boostSpeed: number;
  boostDuration: number;
  /** JUMP: how long the phase-through lasts once triggered. `_Temps_Bonus_Saut`. */
  jumpDuration: number;
}

export const DEFAULT_CONFIG: GameConfig = {
  grid: { x: 5, y: 5, width: 1000, height: 700 },
  tickSeconds: 0.05,
  initialSpeed: 10,
  acceleration: 0.02,
  trailLife: 150,
  victoryScore: 14,
  bonus: {
    maxOnGrid: 12,
    size: 20,
    respawnFirst: 150,
    respawnMin: 20,
    respawnMax: 250,
    boostSpeed: 15,
    boostDuration: 25,
    jumpDuration: 10,
  },
};

/** Bike dimensions (portrait): 5 wide, 10 long. `MotoConstructeur(..., 5, 10, ...)`. */
export const BIKE_SHORT = 5;
export const BIKE_LONG = 10;

/**
 * Spawn positions by player count.
 * From `SDLJeuInitN` (`SDL.c:213`): two layouts, one for <= 4 players, one for
 * 5 to 8. Coordinates are relative to the play area origin.
 */
const SPAWNS_LE4: readonly SpawnPoint[] = [
  { x: 499.5, y: 51.5, dir: Direction.Down },
  { x: 504.5, y: 651.5, dir: Direction.Up },
  { x: 201.5, y: 356.5, dir: Direction.Right },
  { x: 801.5, y: 351.5, dir: Direction.Left },
];

const SPAWNS_GE5: readonly SpawnPoint[] = [
  { x: 404.5, y: 51.5, dir: Direction.Down },
  { x: 810.5, y: 254.5, dir: Direction.Left },
  { x: 604.5, y: 661.5, dir: Direction.Up },
  { x: 201.5, y: 457.5, dir: Direction.Right },
  { x: 607.5, y: 51.5, dir: Direction.Down },
  { x: 404.5, y: 661.5, dir: Direction.Up },
  { x: 201.5, y: 254.5, dir: Direction.Right },
  { x: 810.5, y: 457.5, dir: Direction.Left },
];

export function spawnPoints(playerCount: number): readonly SpawnPoint[] {
  const table = playerCount <= 4 ? SPAWNS_LE4 : SPAWNS_GE5;
  return table.slice(0, playerCount);
}

export const COLOR_FOR_PLAYER: readonly PlayerColor[] = [
  "orange",
  "blue",
  "red",
  "green",
  "purple",
  "darkblue",
  "yellow",
  "white",
];

/** Player-count range offered by the setup screen. `_Nombre_de_Joueur` capped at 8. */
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 8;

/** "Rounds to win" choices offered in setup. `_Score_de_Victoire` was 14. */
export const VICTORY_SCORE_CHOICES: readonly number[] = [5, 10, 14, 20];

/** Countdown length in seconds before each round (2013 `SDLDecompte` was 3). */
export const COUNTDOWN_SECONDS = 3;

/** Width of the in-canvas side panel (scoreboard). 2013 interface panel was 280. */
export const PANEL_WIDTH = 240;
